import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Camera, ArrowRight, Euro, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate, fmtDT } from "@/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import QuickCheck from "@/components/QuickCheck";
import WeekendTasks from "@/components/WeekendTasks";

function Kpi({ label, value, sub, testid, subClass, subTestid, valueClass, onClick, warn }) {
  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      data-testid={onClick ? `${testid}-btn` : undefined}
      className={`rounded-2xl border bg-white p-4 sm:p-5 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200 ${warn ? "border-amber-300" : "border-slate-200"} ${onClick ? "cursor-pointer hover:border-sky-400" : ""}`}
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p data-testid={testid} className={`font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-1 font-mono tabular-nums ${valueClass || ""}`}>
        {value}
        {warn && (
          <span data-testid={`${testid}-warn`} title="Règlement(s) en attente sur les prestations de la semaine"
            className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 align-middle">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-700" />
          </span>
        )}
      </p>
      {sub && <p data-testid={subTestid} className={subClass || "text-xs text-slate-500 mt-1"}>{sub}</p>}
      {onClick && <p className="text-[11px] font-semibold text-sky-600 mt-1">Voir le détail →</p>}
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid={testid}>
      <button aria-label="Fermer" data-testid={`${testid}-backdrop`} className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg max-h-[88vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5 space-y-4 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading text-lg font-extrabold tracking-tight">{title}</h3>
          <button data-testid={`${testid}-close`} onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 shrink-0">Fermer</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const telHref = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  if (!d) return "";
  return d.startsWith("0") ? `tel:+33${d.slice(1)}` : `tel:+${d}`;
};

const KIND_BADGE = {
  acompte: { label: "ACOMPTE", cls: "bg-sky-100 text-sky-800" },
  solde: { label: "SOLDE", cls: "bg-emerald-100 text-emerald-800" },
  recette: { label: "RECETTE", cls: "bg-slate-100 text-slate-700" },
};

function CaModal({ period, title, onClose, navigate }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/stats/ca-detail?period=${period}`).then((r) => setData(r.data)).catch(() => toast.error("Détail indisponible")); }, [period]);
  return (
    <Modal title={title} onClose={onClose} testid="dashboard-ca-modal">
      {!data && <p className="text-sm text-slate-400">Chargement…</p>}
      {data && (
        <>
          {data.from && data.to && (
            <p className="text-xs text-slate-500" data-testid="dashboard-ca-range">Encaissements réels du {fmtDate(data.from)} au {fmtDate(data.to)} — la date de prestation n'influence pas la période.</p>
          )}
          {(() => { const pending = (data.prestations || []).filter((p) => p.reste > 0); return pending.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3.5 space-y-2" data-testid="dashboard-ca-pending">
              <p className="text-xs font-extrabold uppercase tracking-wider text-amber-800 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Règlements à encaisser ({pending.length})</p>
              {pending.map((p, i) => (
                <button key={p.reservation_id} type="button" data-testid={`dashboard-ca-pending-${i}`}
                  onClick={() => p.reservation_id && navigate(`/reservations/${p.reservation_id}`)}
                  className="w-full text-left rounded-lg bg-white border border-amber-200 p-3 space-y-0.5 hover:border-amber-400 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-sm">{p.client}</p>
                    <p className="font-mono font-extrabold tabular-nums text-amber-700">{fmtEUR(p.reste)}</p>
                  </div>
                  <p className="text-xs text-slate-600">{p.prestation} — prestation : {p.event_label}</p>
                </button>
              ))}
            </div>
          ); })()}

          {(data.prestations || []).length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500">Prestations de la période</p>
              {data.prestations.map((p, i) => (
                <button key={p.reservation_id} type="button" data-testid={`dashboard-ca-prestation-${i}`}
                  onClick={() => p.reservation_id && navigate(`/reservations/${p.reservation_id}`)}
                  className="w-full text-left rounded-xl border border-slate-200 p-3.5 space-y-1 hover:border-sky-300 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-sm">{p.client}</p>
                    <p className="font-mono font-extrabold tabular-nums">{fmtEUR(p.total)}</p>
                  </div>
                  <p className="text-xs text-slate-600">{p.event_label}{p.event_type ? ` — ${p.event_type}` : ""} · {p.prestation}</p>
                  <p className="text-xs font-semibold flex items-center gap-1 flex-wrap">
                    <span className="text-emerald-700">Encaissé : {fmtEUR(p.paid)}</span>
                    {p.reste > 0 ? (
                      <span className="text-amber-700 inline-flex items-center gap-0.5" data-testid={`dashboard-ca-prestation-reste-${i}`}>
                        · Reste à encaisser : {fmtEUR(p.reste)} <AlertTriangle className="h-3 w-3" />
                      </span>
                    ) : <span className="text-slate-400">· Soldé</span>}
                  </p>
                </button>
              ))}
            </div>
          )}

          <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500 pt-1">Encaissements de la période</p>
          {data.moves.length === 0 && <p className="text-sm text-slate-400" data-testid="dashboard-ca-empty">Aucun encaissement sur cette période.</p>}
          <div className="space-y-2">
            {data.moves.map((m, i) => {
              const b = KIND_BADGE[m.kind] || KIND_BADGE.recette;
              return (
                <button key={i} type="button" data-testid={`dashboard-ca-move-${i}`}
                  onClick={() => m.reservation_id && navigate(`/reservations/${m.reservation_id}`)}
                  className="w-full text-left rounded-xl border border-slate-200 p-3.5 space-y-1.5 hover:border-sky-300 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-sm">{m.client}</p>
                    <p className="font-mono font-extrabold tabular-nums">{fmtEUR(m.amount)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider ${b.cls}`} data-testid={`dashboard-ca-move-kind-${i}`}>{b.label}</span>
                    {m.urssaf_free && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-violet-100 text-violet-800">Hors CA déclaré</span>}
                  </div>
                  <p className="text-xs text-slate-600">Encaissé le {m.paid_label}</p>
                  {m.event_label && <p className="text-xs text-slate-600">Prestation : {m.event_label}{m.event_type ? ` — ${m.event_type}` : ""}</p>}
                  <p className="text-xs font-semibold text-slate-700">{m.prestation}</p>
                </button>
              );
            })}
          </div>
          <p className="text-sm font-bold pt-2 border-t border-slate-200">
            Total encaissé : <span className="font-mono tabular-nums" data-testid="dashboard-ca-total">{fmtEUR(data.total)}</span>
            {data.declared !== data.total && (
              <span className="block text-xs font-semibold text-slate-500 mt-0.5">dont CA déclaré (URSSAF) : <span className="font-mono" data-testid="dashboard-ca-declared">{fmtEUR(data.declared)}</span></span>
            )}
          </p>
        </>
      )}
    </Modal>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [rev, setRev] = useState(null);
  const [contractAlerts, setContractAlerts] = useState(null);
  const [boothNames, setBoothNames] = useState(["Photobooth 1", "Photobooth 2", "Photobooth 3"]);
  const [modal, setModal] = useState(null); // "ca-week" | "ca-month" | "ca-year" | "deliveries" | "retraits"
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/stats/dashboard"), api.get("/settings"), api.get("/contracts/alerts"), api.get("/stats/revenue")]).then(([s, cfg, ca, rv]) => {
      setStats(s.data);
      setRev(rv.data);
      setBoothNames(cfg.data.booth_names);
      setContractAlerts(ca.data);
    }).catch(() => {});
  }, []);

  if (!stats) return <p className="text-slate-400" data-testid="dashboard-loading">Chargement…</p>;

  const deliveries = stats.week.weekend_deliveries || [];
  const directDeliveries = deliveries.filter((d) => !d.stephane);
  const stephaneDeliveries = deliveries.filter((d) => d.stephane);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="space-y-6" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Bonjour</h1>
          <p className="text-slate-500 text-sm mt-1">Voici l'activité de vos photobooths.</p>
        </div>
        <Button
          size="lg"
          data-testid="btn-new-booking-dashboard"
          onClick={() => navigate("/reservations/nouvelle")}
          className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" /> Nouvelle réservation
        </Button>
      </div>

      <QuickCheck />

      {contractAlerts && (contractAlerts.en_retard > 0 || contractAlerts.a_completer > 0) && (
        <button
          data-testid="contracts-alert-banner"
          onClick={() => navigate("/contrats")}
          className={`w-full text-left rounded-2xl border-2 p-4 transition-colors ${contractAlerts.en_retard > 0 ? "border-rose-300 bg-rose-50 hover:bg-rose-100" : "border-amber-300 bg-amber-50 hover:bg-amber-100"}`}
        >
          <p className={`font-heading font-extrabold ${contractAlerts.en_retard > 0 ? "text-rose-700" : "text-amber-800"}`}>
            {contractAlerts.en_retard > 0
              ? `${contractAlerts.en_retard} contrat${contractAlerts.en_retard > 1 ? "s" : ""} en attente de signature à moins de 7 jours de l'événement`
              : `${contractAlerts.a_completer} contrat${contractAlerts.a_completer > 1 ? "s" : ""} à compléter avant envoi`}
          </p>
          <p className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">Voir le suivi des contrats <ArrowRight className="h-3 w-3" /></p>
        </button>
      )}

      {stats.soldes_en_retard && stats.soldes_en_retard.length > 0 && (
        <div data-testid="soldes-retard-banner" className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-4 space-y-2">
          <p className="font-heading font-extrabold text-rose-700 flex items-center gap-1.5">
            <Euro className="h-4 w-4" />
            {stats.soldes_en_retard.length} solde{stats.soldes_en_retard.length > 1 ? "s" : ""} en attente sur une prestation passée — {fmtEUR(stats.soldes_en_retard.reduce((s, x) => s + x.reste, 0))} à encaisser
          </p>
          <ul className="space-y-1">
            {stats.soldes_en_retard.map((s, i) => (
              <li key={s.id}>
                <button type="button" data-testid={`solde-retard-${i}`} onClick={() => navigate(`/reservations/${s.id}`)}
                  className="text-sm text-rose-900 underline underline-offset-2 hover:text-rose-700 transition-colors">
                  {s.client} — prestation du {fmtDate(s.event_date)} (il y a {s.days} j) — reste à encaisser {fmtEUR(s.reste)}
                </button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-rose-800/70">Cliquez sur une ligne pour ouvrir la réservation et valider l'encaissement avec sa date réelle.</p>
        </div>
      )}

      {stats.stock && stats.stock.level !== "green" && (
        <button
          data-testid="stock-alert-banner"
          onClick={() => navigate("/stock")}
          className={`w-full text-left rounded-2xl border-2 p-4 transition-colors ${stats.stock.level === "red" ? "border-rose-300 bg-rose-50 hover:bg-rose-100" : "border-amber-300 bg-amber-50 hover:bg-amber-100"}`}
        >
          <p className={`font-heading font-extrabold ${stats.stock.level === "red" ? "text-rose-700" : "text-amber-800"}`}>
            ⚠️ {stats.stock.level === "red" ? "Stock consommable insuffisant — commande à prévoir" : "Stock consommable à commander prochainement"}
          </p>
          {stats.stock.rupture && (
            <p className="text-sm text-slate-700 mt-0.5">
              Rupture prévisionnelle avant le <strong>{fmtDate(stats.stock.rupture.date)}</strong>
              {stats.stock.order_by && <> — commande recommandée avant le <strong>{fmtDate(stats.stock.order_by)}</strong></>}
            </p>
          )}
          <p className="text-xs text-slate-500 mt-1">Voir le stock consommables →</p>
        </button>
      )}

      {stats.stock && (stats.stock.acc_alert_count || 0) > 0 && (
        <button
          data-testid="stock-accessories-alert"
          onClick={() => navigate("/stock")}
          className="w-full text-left rounded-2xl border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 p-3 transition-colors"
        >
          <p className="text-sm font-semibold text-amber-800">
            ⚠️ Stock accessoires à vérifier — {stats.stock.acc_alert_count} référence{stats.stock.acc_alert_count > 1 ? "s" : ""} sous le seuil minimum
            {stats.stock.acc_alert_names?.length ? ` (${stats.stock.acc_alert_names.join(", ")})` : ""}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">Voir le stock →</p>
        </button>
      )}

      <section>
        <h2 className="font-heading text-base md:text-lg font-bold mb-3">Cette semaine</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Kpi label="Chiffre d'affaires de la semaine" value={fmtEUR(stats.week.ca)} testid="kpi-revenue-amount" sub={`Encaissé : ${fmtEUR(stats.week.encaisse ?? 0)} · Bénéfice estimé (prestations) : ${fmtEUR(stats.week.profit ?? 0)}${(stats.week.reste ?? 0) > 0 ? ` · Reste à encaisser : ${fmtEUR(stats.week.reste)}` : ""}`} subClass="text-xs text-amber-600 mt-1" subTestid="kpi-benefit-amount" warn={(stats.week.reste ?? 0) > 0} onClick={() => setModal("ca-week")} />
          <Kpi label="Photobooths dispos" value={`${stats.week.booths_available} / 3`} testid="kpi-available-booths" sub={stats.week.booths_available === 1 ? "Analyse logistique requise pour la 3e location" : undefined} />
          {directDeliveries.length > 0 && (
            <Kpi label="Livraisons à venir" value={directDeliveries.length} testid="kpi-next-deliveries" valueClass="text-emerald-600" sub="Par Photobooth54" onClick={() => setModal("deliveries")} />
          )}
          {stephaneDeliveries.length > 0 && (
            <Kpi label={stephaneDeliveries.length > 1 ? "Livraisons par Stéphane" : "Livraison par Stéphane"} value={stephaneDeliveries.length} testid="kpi-stephane-deliveries" valueClass="text-violet-600" sub="Déléguée(s) — pas de livraison directe" />
          )}
          <Kpi
            label={(stats.week.retraits || []).length > 1 ? "Retraits à Lesménils" : "Retrait à Lesménils"}
            value={(stats.week.retraits || []).length}
            testid="kpi-retraits"
            valueClass={(stats.week.retraits || []).length > 0 ? "text-rose-600" : undefined}
            onClick={(stats.week.retraits || []).length > 0 ? () => setModal("retraits") : undefined}
          />
        </div>
      </section>

      <WeekendTasks />

      {stats.prev_month && (
        <div data-testid="prev-month-ca" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0">
              <Euro className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Chiffre d'affaires du mois précédent</p>
              <p className="font-heading font-extrabold text-lg text-slate-900">{stats.prev_month.label} — <span className="font-mono tabular-nums">{fmtEUR(stats.prev_month.ca)}</span></p>
            </div>
          </div>
          <Button variant="outline" size="sm" data-testid="prev-month-ca-detail" onClick={() => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); navigate(`/ca?ca_detail=${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); }} className="border-emerald-300 text-emerald-800 hover:bg-emerald-100">
            Voir le détail
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <h2 className="font-heading text-base md:text-lg font-bold mb-3">Ce mois</h2>
          <div className="flex gap-8">
            <div><p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Locations</p><p data-testid="kpi-month-count" className="font-heading text-2xl font-extrabold font-mono tabular-nums">{stats.month.count}</p></div>
            <button type="button" data-testid="kpi-month-revenue-btn" onClick={() => setModal("ca-month")} className="text-left group">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">CA encaissé</p>
              <p data-testid="kpi-month-revenue" className="font-heading text-2xl font-extrabold font-mono tabular-nums group-hover:text-sky-700 transition-colors">{fmtEUR(rev?.month?.encaisse ?? stats.month.ca)}</p>
              <p className="text-[11px] font-semibold text-sky-600">Voir le détail →</p>
            </button>
          </div>
        </section>
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <h2 className="font-heading text-base md:text-lg font-bold mb-3">Cette année</h2>
          <div className="flex gap-8">
            <div><p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Locations</p><p data-testid="kpi-year-count" className="font-heading text-2xl font-extrabold font-mono tabular-nums">{stats.year.count}</p></div>
            <button type="button" data-testid="kpi-year-revenue-btn" onClick={() => setModal("ca-year")} className="text-left group">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">CA encaissé</p>
              <p data-testid="kpi-year-revenue" className="font-heading text-2xl font-extrabold font-mono tabular-nums group-hover:text-sky-700 transition-colors">{fmtEUR(rev?.year?.encaisse ?? stats.year.ca)}</p>
              <p className="text-[11px] font-semibold text-sky-600">Voir le détail →</p>
            </button>
          </div>
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <h2 className="font-heading text-base md:text-lg font-bold mb-3">Accès rapide</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[["Agenda", "/agenda", "quick-agenda"], ["Réservations", "/reservations", "quick-reservations"], ["Chiffre d'affaires", "/ca", "quick-revenue"], ["Paramètres", "/parametres", "quick-settings"]].map(([label, to, tid]) => (
            <button
              key={to}
              data-testid={tid}
              onClick={() => navigate(to)}
              className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium hover:border-sky-400 hover:text-sky-700 transition-colors"
            >
              {label} <ArrowRight className="h-4 w-4" />
            </button>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-400 flex items-center gap-1"><Camera className="h-3 w-3" /> {boothNames.join(" · ")}</p>
      </section>

      {modal === "ca-week" && <CaModal period="week" title="Cette semaine — détail du chiffre d'affaires" onClose={() => setModal(null)} navigate={navigate} />}
      {modal === "ca-month" && <CaModal period="month" title="Ce mois — détail du chiffre d'affaires" onClose={() => setModal(null)} navigate={navigate} />}
      {modal === "ca-year" && <CaModal period="year" title="Cette année — détail du chiffre d'affaires" onClose={() => setModal(null)} navigate={navigate} />}

      {modal === "deliveries" && (
        <Modal title={`Livraisons à venir (${directDeliveries.length})`} onClose={() => setModal(null)} testid="dashboard-deliveries-modal">
          <div className="space-y-3">
            {directDeliveries.map((d, i) => {
              const addr = [d.address, [d.postal_code, d.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
              return (
                <div key={d.id} data-testid={`dashboard-delivery-${i}`} className="rounded-xl border border-slate-200 p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-bold">{d.client}</p>
                    <p className="text-xs font-semibold text-sky-700 text-right shrink-0" data-testid={`dashboard-delivery-when-${i}`}>
                      {fmtDT(d.datetime)}{d.slot ? ` — ${d.slot}` : ""}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {d.phone
                      ? <a href={telHref(d.phone)} data-testid={`dashboard-delivery-phone-${i}`} className="flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 text-white text-sm font-bold h-11">Appeler</a>
                      : <span className="flex items-center justify-center rounded-lg bg-slate-100 text-slate-400 text-xs h-11">Pas de téléphone</span>}
                    {d.email
                      ? <a href={`mailto:${d.email}`} data-testid={`dashboard-delivery-email-${i}`} className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-bold h-11">E-mail</a>
                      : <span className="flex items-center justify-center rounded-lg bg-slate-100 text-slate-400 text-xs h-11">Pas d'e-mail</span>}
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Adresse de livraison</p>
                    {d.address && <p className="text-sm text-slate-800">{d.address}</p>}
                    <p className="text-sm font-semibold text-slate-800">{[d.postal_code, d.city].filter(Boolean).join(" ") || "—"}</p>
                    {addr && (
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`} target="_blank" rel="noopener noreferrer"
                        data-testid={`dashboard-delivery-map-${i}`} className="inline-flex items-center gap-1 text-sm font-bold text-sky-700 underline underline-offset-2">
                        Itinéraire <ArrowRight className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Prestation</p>
                    {(d.prestation || []).map((p, j) => <p key={j} className="text-sm text-slate-800" data-testid={`dashboard-delivery-prestation-${i}-${j}`}>• {p}</p>)}
                    {d.event_date && <p className="text-xs text-slate-500">Événement : {fmtDate(d.event_date)}{d.event_type ? ` — ${d.event_type}` : ""}</p>}
                  </div>
                  <button type="button" data-testid={`dashboard-delivery-open-${i}`} onClick={() => navigate(`/reservations/${d.id}`)}
                    className="w-full rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                    Ouvrir la réservation
                  </button>
                </div>
              );
            })}
          </div>
        </Modal>
      )}

      {modal === "retraits" && (
        <Modal title={`Retraits à Lesménils (${(stats.week.retraits || []).length})`} onClose={() => setModal(null)} testid="dashboard-retraits-modal">
          <p className="text-xs text-slate-500">Retrait client à Lesménils (54700) — aucune livraison.</p>
          <div className="space-y-3">
            {(stats.week.retraits || []).map((r, i) => (
              <div key={r.id} data-testid={`dashboard-retrait-${i}`} className="rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-bold">{r.client}</p>
                  <p className="text-xs font-semibold text-rose-700 text-right shrink-0" data-testid={`dashboard-retrait-when-${i}`}>
                    {fmtDT(r.datetime)}{r.slot ? ` — ${r.slot}` : ""}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {r.phone
                    ? <a href={telHref(r.phone)} data-testid={`dashboard-retrait-phone-${i}`} className="flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 text-white text-sm font-bold h-11">Appeler</a>
                    : <span className="flex items-center justify-center rounded-lg bg-slate-100 text-slate-400 text-xs h-11">Pas de téléphone</span>}
                  {r.email
                    ? <a href={`mailto:${r.email}`} data-testid={`dashboard-retrait-email-${i}`} className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-bold h-11">E-mail</a>
                    : <span className="flex items-center justify-center rounded-lg bg-slate-100 text-slate-400 text-xs h-11">Pas d'e-mail</span>}
                </div>
                <div className="space-y-1">
                  {(r.prestation || []).map((p, j) => <p key={j} className="text-sm text-slate-800" data-testid={`dashboard-retrait-prestation-${i}-${j}`}>• {p}</p>)}
                </div>
                <button type="button" data-testid={`dashboard-retrait-open-${i}`} onClick={() => navigate(`/reservations/${r.id}`)}
                  className="w-full rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                  Ouvrir la réservation
                </button>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </motion.div>
  );
}
